// Frontend API layer - all MovieMe DB calls go through Neon API endpoints with JWT auth
import { getCurrentUserId, getAuthToken } from './supabaseClient';
import { DESIGN_PREVIEW, previewLists } from './designPreview';
import { apiFetch, ApiError } from './apiClient';

async function withToken(): Promise<string | null> {
  return getAuthToken();
}

// Frontend database functions - all calls go through API endpoints
export async function addToWatchlist(movieData: {
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
}) {
  if (DESIGN_PREVIEW) return previewLists.addToWatchlist(movieData);
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  try {
    return await apiFetch('/api/watchlist', {
      method: 'POST',
      token: await withToken(),
      body: {
        action: 'add',
        movieData
      }
    });
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 409) return null;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error('Authentication required');
    }
    if (error instanceof Error && error.message.includes('unique')) {
      return null;
    }
    throw error instanceof ApiError ? new Error('Failed to add to watchlist') : error;
  }
}

export async function removeFromWatchlist(tmdbId: number) {
  if (DESIGN_PREVIEW) return previewLists.removeFromWatchlist(tmdbId);
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  try {
    await apiFetch('/api/watchlist', {
      method: 'POST',
      token: await withToken(),
      body: {
        action: 'remove',
        tmdb_id: tmdbId
      }
    });
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 401) {
      throw new Error('Authentication required');
    }
    throw error instanceof ApiError ? new Error('Failed to remove from watchlist') : error;
  }
}

export async function isInWatchlist(tmdbId: number): Promise<boolean> {
  if (DESIGN_PREVIEW) return previewLists.isInWatchlist(tmdbId);
  const userId = await getCurrentUserId();
  if (!userId) return false;

  try {
    const data = await apiFetch<{ exists: boolean }>(`/api/watchlist?tmdb_id=${tmdbId}`, {
      token: await withToken(),
    });
    return Boolean(data?.exists);
  } catch {
    return false;
  }
}

export async function getWatchlist() {
  if (DESIGN_PREVIEW) return previewLists.getWatchlist();
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  try {
    return await apiFetch('/api/watchlist', {
      token: await withToken(),
    });
  } catch {
    return [];
  }
}

export async function markAsWatched(movieData: {
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
  rating?: number;
  review?: string;
}) {
  if (DESIGN_PREVIEW) return previewLists.markAsWatched(movieData);
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  try {
    return await apiFetch('/api/watched', {
      method: 'POST',
      token: await withToken(),
      body: {
        action: 'add',
        movieData
      }
    });
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 409) return null;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error('Authentication required');
    }
    if (error instanceof Error && error.message.includes('unique')) {
      return null;
    }
    throw error instanceof ApiError ? new Error('Failed to mark as watched') : error;
  }
}

export async function removeFromWatched(tmdbId: number) {
  if (DESIGN_PREVIEW) return previewLists.removeFromWatched(tmdbId);
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  try {
    await apiFetch('/api/watched', {
      method: 'POST',
      token: await withToken(),
      body: {
        action: 'remove',
        tmdb_id: tmdbId
      }
    });
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 401) {
      throw new Error('Authentication required');
    }
    throw error instanceof ApiError ? new Error('Failed to remove from watched') : error;
  }
}

export async function isWatched(tmdbId: number): Promise<boolean> {
  if (DESIGN_PREVIEW) return previewLists.isWatched(tmdbId);
  const userId = await getCurrentUserId();
  if (!userId) return false;

  try {
    const data = await apiFetch<{ exists: boolean }>(`/api/watched?tmdb_id=${tmdbId}`, {
      token: await withToken(),
    });
    return Boolean(data?.exists);
  } catch {
    return false;
  }
}

export async function getWatchedMovies() {
  if (DESIGN_PREVIEW) return previewLists.getWatched();
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  try {
    return await apiFetch('/api/watched', {
      token: await withToken(),
    });
  } catch {
    return [];
  }
}
